import AppIcon from "../components/AppIcon";
import { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import { auth, database, storage } from "../constants/firebase";
import {
    ref,
    remove,
    get,
    runTransaction
} from "firebase/database";
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    reauthenticateWithPopup,
    GoogleAuthProvider,
    deleteUser,
    reauthenticateWithCredential as reauthCredFn, // alias for clarity if needed
    signOut
} from "firebase/auth";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import {
    deleteObject,
    ref as storageRef,
    listAll,
} from "firebase/storage";
import { clearFcmToken, deleteBunnyCollections } from "../utils/utils";
import { ROUTES } from "../App";

const DeleteAccount = () => {
    const navigate = useMoveTo();
    const { currentUser, refreshUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Keep track of whether a native reauth is in-progress so we don't double-send
    const [awaitingNativeReauth, setAwaitingNativeReauth] = useState(false);

    // Create a Firebase storage ref from a download URL
    const refFromDownloadURL = (url) => {
        return storageRef(storage, url.replace(/.*\/o\//, '').split('?')[0]);
    };

    const handleLogout = useCallback(() => {
        const uid = localStorage.getItem("uid");
        clearFcmToken(uid);
        localStorage.removeItem("uid");
        localStorage.removeItem("sessionId");
        refreshUser();
        setTimeout(() => {
            navigate(ROUTES.SIGN_IN, { replace: true });
        }, 500);
    }, [navigate, refreshUser]);

    // Final deletion logic (called after reauth has succeeded)
    const handleDeleteAccountFinal = useCallback(async () => {
        setIsSubmitting(true);
        if (!auth.currentUser) {
            console.warn("No auth.currentUser available in final delete.");
            return;
        }
        const user = auth.currentUser;
        const uid = user.uid;

        console.log("🗑️ Proceeding with deletion for user:", uid);

        try {
            // 1) Delete common profile files
            const profileFiles = [
                `users/${uid}/profile.jpg`,
                `users/${uid}/portfolio/banner.jpg`,
            ];

            const profileDeletes = profileFiles.map(async (path) => {
                try {
                    // If the path looks like a URL, use refFromURL; otherwise treat as storage path
                    if (typeof path === "string" && path.startsWith("http")) {
                        await deleteObject(refFromDownloadURL(path));
                    } else {
                        await deleteObject(storageRef(storage, path));
                    }
                } catch (e) {
                    // file may not exist — log and continue
                    console.log(`⚠️ Profile file not found or error deleting: ${path}`, e?.message || e);
                }
            });
            await Promise.all(profileDeletes);

            // 2) Delete / update Groups
            const groupsSnap = await get(ref(database, "Groups"));
            const groupOps = [];
            groupsSnap.forEach((snap) => {
                const group = snap.val();
                const groupId = snap.key;
                if (!groupId) return;

                if (group?.createdBy === uid) {
                    groupOps.push(remove(ref(database, `Groups/${groupId}`)));
                }
                if (group?.members && group.members[uid]) {
                    groupOps.push(remove(ref(database, `Groups/${groupId}/members/${uid}`)));
                }
            });
            await Promise.all(groupOps);

            // 3) Delete / update Communities
            const communitiesSnap = await get(ref(database, "Communities"));
            const communityOps = [];
            communitiesSnap.forEach((snap) => {
                const community = snap.val();
                const communityId = snap.key;
                if (!communityId) return;

                // If host -> delete community record + image (if any)
                if (community?.hostId === uid) {
                    if (community?.imageUrl) {
                        // If imageUrl is a download URL or storage path — support both
                        communityOps.push((async () => {
                            try {
                                if (community.imageUrl.startsWith("http")) {
                                    await deleteObject(refFromDownloadURL(community.imageUrl));
                                } else {
                                    await deleteObject(storageRef(storage, community.imageUrl));
                                }
                            } catch (e) {
                                console.log(`⚠️ Could not delete community image for ${communityId}`, e?.message || e);
                            }
                        })());
                    }
                    communityOps.push(remove(ref(database, `Communities/${communityId}`)));
                } else {
                    // If user is a member -> remove membership and decrement memberCount via transaction
                    if (community?.members && community.members[uid]) {
                        communityOps.push(remove(ref(database, `Communities/${communityId}/members/${uid}`)));
                        communityOps.push(remove(ref(database, `Users/${uid}/joinedCommunities/${communityId}`)));
                        // Use transaction to decrement memberCount
                        communityOps.push(runTransaction(ref(database, `Communities/${communityId}/memberCount`), (count) => {
                            if (count === null || typeof count === "undefined") return 0;
                            // ensure not negative
                            return Math.max(0, (count || 0) - 1);
                        }));
                    }
                }
            });
            await Promise.all(communityOps);

            // 4) Delete hostStories DB node (and its files in storage)
            try {
                await remove(ref(database, `hostStories/${uid}`));
            } catch (e) {
                console.log("⚠️ Error removing hostStories DB node:", e?.message || e);
            }

            // Delete any files under hostStories/{uid} in storage
            try {
                const userFolderRef = storageRef(storage, `hostStories/${uid}`);
                const allFiles = await listAll(userFolderRef);
                const fileDeletes = allFiles.items.map(fileRef => deleteObject(fileRef).catch(err => {
                    console.log("⚠️ error deleting hostStories file", err?.message || err);
                }));
                await Promise.all(fileDeletes);
            } catch (e) {
                // listAll can throw if folder doesn't exist — ignore
                console.log("⚠️ No hostStories files to delete or error while listing:", e?.message || e);
            }

            // 5) Delete Listings (and their files)
            const listingsSnap = await get(ref(database, "Listings"));
            const listingOps = [];
            listingsSnap.forEach((snap) => {
                const listing = snap.val();
                const listingId = snap.key;
                if (!listingId) return;

                if (listing?.userId === uid) {
                    // coverImageUrl and fileUrl might be download URLs or storage paths
                    if (listing.coverImageUrl) {
                        listingOps.push((async () => {
                            try {
                                if (listing.coverImageUrl.startsWith("http")) {
                                    await deleteObject(refFromDownloadURL(listing.coverImageUrl));
                                } else {
                                    await deleteObject(storageRef(storage, listing.coverImageUrl));
                                }
                            } catch (e) {
                                console.log(`⚠️ Could not delete listing cover for ${listingId}`, e?.message || e);
                            }
                        })());
                    }
                    if (listing.fileUrl) {
                        listingOps.push((async () => {
                            try {
                                if (listing.fileUrl.startsWith("http")) {
                                    await deleteObject(refFromDownloadURL(listing.fileUrl));
                                } else {
                                    await deleteObject(storageRef(storage, listing.fileUrl));
                                }
                            } catch (e) {
                                console.log(`⚠️ Could not delete listing file for ${listingId}`, e?.message || e);
                            }
                        })());
                    }
                    listingOps.push(remove(ref(database, `Listings/${listingId}`)));
                }
            });
            await Promise.all(listingOps);

            // 6) Delete user's Bunny Stream collections (external) - await safely
            try {
                await deleteBunnyCollections(uid);
            } catch (e) {
                console.log("⚠️ deleteBunnyCollections failed:", e?.message || e);
            }

            // 7) DELETE PRIVATE CHATS OF THE USER
            try {
                const privateChatsSnap = await get(ref(database, "privateChats"));

                const privateOps = [];
                privateChatsSnap.forEach((snap) => {
                    const chatId = snap.key;
                    const messages = snap.val();

                    if (!chatId) return;

                    // ChatID contains two UIDs => if user's UID is part of it, delete whole chat
                    if (chatId.includes(uid)) {
                        privateOps.push(remove(ref(database, `privateChats/${chatId}`)));
                    }
                });

                // Remove from requestChats also
                privateOps.push(remove(ref(database, `Users/${uid}/requestChats`)));

                await Promise.all(privateOps);
                console.log("🗑️ Private chats deleted");
            } catch (e) {
                console.log("⚠️ Error deleting private chats:", e?.message || e);
            }

            // 8) REMOVE USER FROM GROUPS + DELETE HIS GROUP MESSAGES
            try {
                const groupsSnap = await get(ref(database, "Groups"));
                const groupOps = [];

                groupsSnap.forEach((snap) => {
                    const groupId = snap.key;
                    const group = snap.val();

                    if (!groupId || !group) return;

                    // If user is a member → remove
                    if (group?.members && group.members[uid]) {
                        groupOps.push(remove(ref(database, `Groups/${groupId}/members/${uid}`)));
                    }

                    // Delete all messages sent by this user in groupChats/{groupId}
                    groupOps.push(
                        (async () => {
                            try {
                                const chatSnap = await get(ref(database, `groupChats/${groupId}`));
                                if (!chatSnap.exists()) return;

                                const messageOps = [];
                                chatSnap.forEach((msgSnap) => {
                                    const msg = msgSnap.val();
                                    if (msg?.senderId === uid) {
                                        messageOps.push(remove(ref(database, `groupChats/${groupId}/${msgSnap.key}`)));
                                    }
                                });

                                await Promise.all(messageOps);
                            } catch (e) {
                                console.log("⚠️ Error deleting user messages in group:", groupId, e);
                            }
                        })()
                    );
                });

                await Promise.all(groupOps);
                console.log("🗑️ Removed user from groups + deleted group messages");
            } catch (e) {
                console.log("⚠️ Error removing user from groups:", e?.message || e);
            }


            // 9) Remove user record from DB
            try {
                await remove(ref(database, `Users/${uid}`));
            } catch (e) {
                console.log("⚠️ Error removing Users record:", e?.message || e);
            }

            // 8) Delete Firebase Auth user
            try {
                await deleteUser(user);
                window.showToast("Account deleted successfully", "success");
            } catch (e) {
                // If deleteUser fails with requires-recent-login, it should have been handled earlier by reauth
                console.error("❌ deleteUser failed:", e?.message || e);
                throw e; // rethrow to surface to outer catch
            }
            console.log("✅ User and all related data deleted");
        } catch (finalErr) {
            console.error("❌ Error during deletion flow:", finalErr?.message || finalErr);
            throw finalErr;
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    // When we require native reauth, RN app will post a success message back. Listen to it.
    useEffect(() => {
        const handleMessage = async (event) => {
            // event.data may be a JSON string (window.message) — parse safely
            let message;
            try {
                message = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
            } catch (err) {
                try {
                    // Some WebViews expose nativeEvent.data (React Native side) — attempt to parse that
                    const raw = event?.nativeEvent?.data ?? event.data;
                    message = typeof raw === "string" ? JSON.parse(raw) : raw;
                } catch (e) {
                    console.log("⚠️ Received non-JSON message (ignored):", event);
                    return;
                }
            }

            if (!message || typeof message !== "object") return;

            // GOOGLE REAUTH SUCCESS
            if (message.type === "GOOGLE_REAUTH_SUCCESS") {
                setAwaitingNativeReauth(false);
                try {
                    const user = auth.currentUser;
                    if (!user) throw new Error("No current user to reauthenticate.");
                    const credential = GoogleAuthProvider.credential(message.token);
                    await reauthenticateWithCredential(user, credential);
                    // Reauth done — proceed to final deletion
                    await handleDeleteAccountFinal();
                    await handleLogout();
                } catch (e) {
                    console.error("❌ Google reauth failed:", e?.message || e);
                    window.showToast?.("Google re-authentication failed. Please try again.", "error");
                } finally {
                    setIsSubmitting(false);
                }
                return;
            }

            // GOOGLE REAUTH ERROR
            if (message.type === "GOOGLE_REAUTH_ERROR") {
                setAwaitingNativeReauth(false);
                setIsSubmitting(false);
                console.error("Native Google reauth error:", message.error);
                window.showToast?.("Google re-authentication failed.", "error");
                return;
            }

            // APPLE REAUTH SUCCESS
            if (message.type === "APPLE_REAUTH_SUCCESS") {
                setAwaitingNativeReauth(false);
                try {
                    const user = auth.currentUser;
                    if (!user) throw new Error("No current user to reauthenticate.");
                    // Create an OAuth credential for Apple
                    const { identityToken, rawNonce } = message;
                    // We will create a credential object using the OAuthProvider constructor if available, else fallback to manual credential creation
                    try {
                        // Preferred: use firebase.auth.OAuthProvider if present (web SDK might differ)
                        // Here we use the generic reauthenticateWithCredential approach using provider credential
                        const OAuthProvider = (await import("firebase/auth")).OAuthProvider;
                        const appleProvider = new OAuthProvider("apple.com");
                        const credential = appleProvider.credential({
                            idToken: identityToken,
                            rawNonce: rawNonce,
                        });
                        await reauthenticateWithCredential(user, credential);
                    } catch (e) {
                        // fallback attempt: sometimes idToken can be used via credentialFromResult patterns (best effort)
                        console.error("⚠️ Apple reauth fallback attempt failed:", e?.message || e);
                        throw e;
                    }

                    await handleDeleteAccountFinal();
                    await handleLogout();
                } catch (e) {
                    console.error("❌ Apple reauth failed:", e?.message || e);
                    window.showToast?.("Apple re-authentication failed. Please try again.", "error");
                } finally {
                    setIsSubmitting(false);
                }
                return;
            }

            if (message.type === "APPLE_REAUTH_ERROR") {
                setAwaitingNativeReauth(false);
                setIsSubmitting(false);
                console.error("Native Apple reauth error:", message.error);
                window.showToast?.("Apple re-authentication failed.", "error");
                return;
            }
        };

        // Listen to both window message (web) and document message (Android WebView)
        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, [handleDeleteAccountFinal, handleLogout]);

    // Primary handler invoked by the button
    const handleDeleteAccount = async () => {
        if (!currentUser) return;
        setIsSubmitting(true);

        try {
            const user = auth.currentUser;
            if (!user) {
                await handleLogout();
                throw new Error("No current authenticated user.");
            }

            const uid = user.uid;
            console.log("🗑️ Delete requested for uid:", uid);

            // Re-auth flows
            if (currentUser.provider === "google") {
                if (window.isInApp) {
                    // Ask native app to reauth the user, app should respond with token -> we handle it in message listener
                    if (!awaitingNativeReauth) {
                        setAwaitingNativeReauth(true);
                        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: "GOOGLE_REAUTH" }));
                        // WAIT: return here; message handler will continue deletion when native responds
                        return;
                    } else {
                        // already awaiting; avoid duplicate
                        return;
                    }
                } else {
                    // Web fallback: popup reauth (works in normal browser)
                    try {
                        await reauthenticateWithPopup(user, new GoogleAuthProvider());
                    } catch (e) {
                        console.error("Web Google reauth failed:", e?.message || e);
                        throw e;
                    }
                }
            } else if (currentUser.provider === "apple") {
                if (window.isInApp) {
                    if (!awaitingNativeReauth) {
                        setAwaitingNativeReauth(true);
                        window.ReactNativeWebView?.postMessage?.(JSON.stringify({ type: "APPLE_REAUTH" }));
                        return; // will continue after native reply
                    } else {
                        return;
                    }
                } else {
                    // Web fallback: try OAuthProvider popup for apple on web
                    try {
                        const { OAuthProvider } = await import("firebase/auth");
                        const provider = new OAuthProvider("apple.com");
                        // reauthenticateWithPopup might work for web
                        await reauthenticateWithPopup(user, provider);
                    } catch (e) {
                        console.error("Web Apple reauth failed:", e?.message || e);
                        throw e;
                    }
                }
            } else {
                // Email/password -> prompt for password and reauthenticate
                const password = prompt("Please enter your account password to confirm:");
                if (!password) {
                    setIsSubmitting(false);
                    return window.showToast?.("Password is required to delete account.", "error");
                }
                const credential = EmailAuthProvider.credential(user.email, password);
                await reauthenticateWithCredential(user, credential);
            }

            // If we reach here it means reauth was done synchronously (web flows). Proceed to delete.
            await handleDeleteAccountFinal();
            await handleLogout();
        } catch (error) {
            console.error("❌ Error during delete request:", error);
            if (error?.code === "auth/requires-recent-login") {
                window.showToast?.("Please log in again and retry.", "error");
            } else {
                window.showToast?.(error?.message || "Error deleting account", "error");
            }
        } finally {
            // For native reauth flow we delay resetting isSubmitting until we get the native response.
            if (!awaitingNativeReauth) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-y-auto">
            <MobileHeader title="Delete Account" isBackBtn={true} />
            <div className="flex">
                <Sidebar />

                <main className="flex-1 h-screen pt-5 pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#23272c] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <AppIcon name="arrowBack" className="w-4 h-4 md:w-5 md:h-5 brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Delete Account</h2>
                        <div />
                    </div>

                    <div className="max-w-2xl mx-auto md:pt-10 pt-3 lg:pt-28 space-y-6">
                        <h3 className="text-xl md:text-2xl font-semibold text-white text-left lg:text-center">Are you sure you want to delete your account?</h3>

                        <div className="space-y-5">
                            <p className="text-md mx-auto text-left lg:text-center">
                                Proceeding with "Delete Account" on Zylen is a permanent action with significant consequences. You will irrevocably lose all content associated with your account. This includes, but isn't limited to, all your messages, any digital content or courses you've purchased or created, images, and chat histories. Furthermore, any communities you currently host will also be permanently removed from the platform. Should you wish to use Zylen again in the future, you'll need to create an entirely new account from scratch. So, think of this as hitting the ultimate "undo" button for your Zylen journey – there's no going back!
                            </p>
                        </div>

                        <button
                            onClick={handleDeleteAccount}
                            className="bg-primary text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Deleting..." : "Delete Account"}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DeleteAccount;
